/* eslint-disable max-lines */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
  ViewChild
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControlName,
  NgControl
} from '@angular/forms';
import { CheckboxValue, Config } from '../../../models/ui.interfaces';
import { SizeType } from '../../../types/common.types';

@Component({
  selector: 'idp-checkbox-standard',
  templateUrl: './checkbox-standard.component.html',
  styleUrls: ['./checkbox-standard.component.scss']
})
export class CheckboxStandardComponent
  implements ControlValueAccessor, AfterViewInit
{
  @ViewChild('inputReference') inputReference!: ElementRef;

  @Input() ariaLabel = '';
  @Input() ariaHidden = false;
  @Input() arrowPosition = 'right';
  @Input() defaultValue = false;
  @Input() error = false;
  @Input() errorMessage = '';
  @Input() errorMessages: Config = {};
  @Input() icon = false;
  @Input() id = '';
  @Input() isDisabled = false;
  @Input() infoToolTip = false;
  @Input() checked = false;
  @Input() labelTitle = '';
  @Input() showFormGrouped = true;
  @Input() submitAttempt = false;
  @Input() uncheckedBackground = 'transparent';
  @Input() boldLabel = false;
  @Input() fontFamily = false;
  @Input() sizeType: SizeType = 'normal';
  @Input() descriptionIcon = false;
  @Input() description = false;
  @Input() labelSrOnly = false;
  @Input() dataCy = '';

  @Output() checkboxFocus: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() clicked: EventEmitter<Event> = new EventEmitter();
  @Output() toolTipClicked: EventEmitter<boolean> = new EventEmitter();
  @Output() value: EventEmitter<CheckboxValue> = new EventEmitter();

  focused = false;
  showErrorToolTip = false;

  private onChange!: (item: { id: string; value: string | false }) => void;
  private onTouched!: () => void;

  constructor(
    private cdr: ChangeDetectorRef,
    @Optional() private controlName: FormControlName,
    @Optional() @Self() private ngControl: NgControl
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  // eslint-disable-next-line complexity
  get errorState() {
    if (
      this.ngControl &&
      this.ngControl.errors &&
      this.ngControl.touched &&
      this.submitAttempt &&
      Object.keys(this.errorMessages).length
    ) {
      this.errorMessage =
        this.errorMessages[Object.keys(this.ngControl.errors)[0]];
    }
    return (
      this.ngControl &&
      this.ngControl.errors &&
      this.submitAttempt &&
      this.ngControl.touched
    );
  }

  emitValue() {
    this.value.emit({
      id: this.id,
      value: this.inputReference.nativeElement.checked
    });
  }

  notify(event: Event) {
    this.clicked.emit(event);
    if (this.onChange) {
      this.onChange({
        id: this.id,
        value: this.inputReference.nativeElement.checked
      });
    }
    this.emitValue();
  }

  iconClick(event: boolean): void {
    this.toolTipClicked.emit(event);
  }

  setFocused() {
    this.focused = true;
    if (this.onTouched) {
      this.onTouched();
    }
  }

  // eslint-disable-next-line complexity
  setInputFocus() {
    this.focused = true;
    if (this.onTouched) {
      this.onTouched();
    }
    if (this.submitAttempt) {
      this.showErrorToolTip = true;
    }
    if (
      (this.error || this.errorState) &&
      this.submitAttempt &&
      this.showErrorToolTip
    ) {
      this.checkboxFocus.emit(this.showErrorToolTip);
    }
  }

  focusOut() {
    this.focused = false;
    this.showErrorToolTip = false;
    this.cdr.detectChanges();
  }

  writeValue(obj: boolean) {
    if (this.checked && this.inputReference) {
      this.inputReference.nativeElement.checked = obj;
    }
  }

  registerOnChange(fn: (item: { id: string; value: string | false }) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  /** function to set default value */
  setDefaultValue() {
    if (this.defaultValue) {
      this.writeValue(this.defaultValue);
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.setDefaultValue();
  }

  getClasses(): string {
    return `idp-checkbox__label--${
      this.sizeType
    } ${this.getStyles()} ${this.getFontFamily()}`;
  }

  getStyles(): string {
    return this.boldLabel ? 'idp-checkbox__label-bold' : '';
  }

  getFontFamily(): string {
    return this.fontFamily ? 'idp-checkbox__label-font-family' : '';
  }
}
